import { useId, type ReactNode } from "react";
import clsx from "clsx";
import type { TripPane } from "../store/uiStore";
import { Button } from "./Button";

type Edge = "left" | "right";

/** Visible name, icon, and the edge each board pane folds toward. */
const TRIP_PANES: Record<
  TripPane,
  { label: string; icon: string; foldsToward: Edge }
> = {
  sidebar: { label: "POIs", icon: "location_on", foldsToward: "left" },
  planner: { label: "Planner", icon: "calendar_month", foldsToward: "left" },
  map: { label: "Map", icon: "map", foldsToward: "right" },
};

/** Width of a collapsed pane rail (`w-10`), for layout maths. */
export const PANE_RAIL_WIDTH = 40;

const opposite = (edge: Edge): Edge => (edge === "left" ? "right" : "left");

/** Points toward `edge` and nudges that way while its button is hovered. */
function Chevron({ edge }: { edge: Edge }) {
  return (
    <span
      className={clsx(
        "material-symbols-outlined text-lg text-tertiary transition-transform group-hover:text-primary motion-reduce:transition-none",
        edge === "left"
          ? "group-hover:-translate-x-0.5"
          : "group-hover:translate-x-0.5",
      )}
      aria-hidden
    >
      {edge === "left" ? "chevron_left" : "chevron_right"}
    </span>
  );
}

interface PaneTitleButtonProps {
  pane: TripPane;
  /** Short context under the title, e.g. the Planner's visible date span. */
  subtitle?: ReactNode;
  /** Omit when the pane cannot be hidden: the title then renders as a plain
   * heading without a chevron. */
  onCollapse?: () => void;
  /** Card treatment for a title that floats over pane content (the Map). */
  floating?: boolean;
}

/**
 * A pane's title, doubling as its hide control. The chevron points toward
 * the edge the pane folds into; the collapsed rail mirrors it.
 */
export function PaneTitleButton({
  pane,
  subtitle,
  onCollapse,
  floating = false,
}: PaneTitleButtonProps) {
  const { label, icon, foldsToward } = TRIP_PANES[pane];
  const labelId = useId();
  const subtitleId = useId();

  const content = (
    <>
      <span
        className="material-symbols-outlined text-lg text-secondary"
        aria-hidden
      >
        {icon}
      </span>
      {/* A toggle keeps its title and chevron whole. The subtitle may be
          wider than the title but gives way first: break-all lets it shrink
          to the title's width, where the one-line clamp ellipsizes it. A
          plain title may be squeezed and truncated like any heading. */}
      <span
        className={clsx("flex flex-col text-left", !onCollapse && "min-w-0")}
      >
        <span className="flex items-center">
          <span
            id={labelId}
            className="truncate text-sm font-bold text-primary"
          >
            {label}
          </span>
          {onCollapse && <Chevron edge={foldsToward} />}
        </span>
        {subtitle && (
          <span
            id={subtitleId}
            className="line-clamp-1 break-all text-2xs text-tertiary"
          >
            {subtitle}
          </span>
        )}
      </span>
    </>
  );

  const floatingClasses =
    "rounded-lg border border-border bg-white/95 px-2 py-1 shadow-sm";

  if (!onCollapse) {
    return (
      <h2
        className={clsx(
          "m-0 flex min-w-0 items-center gap-2",
          floating && clsx(floatingClasses, "pointer-events-none"),
        )}
      >
        {content}
      </h2>
    );
  }

  return (
    <h2 className="m-0 flex">
      <Button
        variant="unstyled"
        aria-labelledby={labelId}
        aria-describedby={subtitle ? subtitleId : undefined}
        aria-expanded
        title={`Hide ${label}`}
        data-pane-toggle={pane}
        onClick={onCollapse}
        className={clsx(
          "group flex cursor-pointer items-center gap-2 font-sans transition-colors hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
          // Outdent left so the icon keeps the header's alignment; the right
          // padding stays in flow so the hover fill clears its neighbour
          floating ? floatingClasses : "-my-1 -ml-2 rounded-lg px-2 py-1",
        )}
      >
        {content}
      </Button>
    </h2>
  );
}

interface CollapsedPaneRailProps {
  pane: TripPane;
  onExpand: () => void;
  /** Draw the divider on the rail's fold edge. Off where another rail
   * already draws the line between them. */
  divider?: boolean;
}

/**
 * The slim, full-height strip a hidden pane leaves behind on the wide
 * layout. It stays in the flow so it never covers another pane's content,
 * and its top band lines up with the other panes' title bars.
 */
export function CollapsedPaneRail({
  pane,
  onExpand,
  divider = true,
}: CollapsedPaneRailProps) {
  const { label, icon, foldsToward } = TRIP_PANES[pane];

  return (
    <Button
      variant="unstyled"
      aria-expanded={false}
      title={`Show ${label}`}
      data-pane-toggle={pane}
      onClick={onExpand}
      className={clsx(
        "group flex w-10 shrink-0 cursor-pointer flex-col items-center gap-3 border-border bg-white pb-4 font-sans text-secondary transition-colors hover:bg-subtle hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
        divider && (foldsToward === "left" ? "border-r" : "border-l"),
      )}
    >
      <span className="flex h-13 w-full shrink-0 items-center justify-center border-b border-border">
        <Chevron edge={opposite(foldsToward)} />
      </span>
      <span className="material-symbols-outlined text-lg" aria-hidden>
        {icon}
      </span>
      <span className="pane-rail-label text-xs font-bold tracking-wide">
        {label}
      </span>
    </Button>
  );
}

/**
 * Narrow-layout opener for a pane that lives in a drawer. A slim tab on the
 * pane's own edge: obvious without taking a column away from the Planner.
 */
export function PaneEdgeHandle({
  pane,
  onOpen,
}: {
  pane: Exclude<TripPane, "planner">;
  onOpen: () => void;
}) {
  const { label, foldsToward } = TRIP_PANES[pane];

  return (
    <Button
      variant="unstyled"
      aria-expanded={false}
      aria-haspopup="dialog"
      title={`Show ${label}`}
      data-pane-toggle={pane}
      onClick={onOpen}
      className={clsx(
        "group absolute top-1/2 z-30 flex w-8 -translate-y-1/2 cursor-pointer flex-col items-center gap-1.5 border border-border bg-white py-2.5 font-sans text-secondary shadow-md transition-colors hover:bg-subtle hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        foldsToward === "left"
          ? "left-0 rounded-r-lg border-l-0"
          : "right-0 rounded-l-lg border-r-0",
      )}
    >
      <Chevron edge={opposite(foldsToward)} />
      <span className="pane-rail-label text-2xs font-bold tracking-wide">
        {label}
      </span>
    </Button>
  );
}
